<script setup lang="ts">
import CurrencyText from './CurrencyText.vue'
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'

// Explainer panel shown behind an ⓘ icon:
//   title (+ optional value on the same baseline) → optional subline →
//   optional tinted note → optional paragraph → equation / breakdown blocks →
//   footer slot.
// Equation rows: operator in a narrow left gutter, label, spacer, right-aligned
// amount; a result row ("=") is emphasised.

export interface InfoSheetRow {
  /** Leading operator ("+", "−", "×", "÷", "="); omitted on a plain first row. */
  operator?: string
  label: string
  value: string
  /** Result row — larger, bolder amount. */
  isResult?: boolean
}

export interface InfoSheetContent {
  title: string
  value?: string
  subline?: string
  /** Tinted lead-in box; omitted (or empty) when the sheet is paragraph-only. */
  note?: string
  /** Body paragraph; omitted (or empty) when the note says it all. */
  description?: string
  rows?: InfoSheetRow[]
  /** Further equations after `rows` (e.g. a token-supply sum, then the price). */
  extraRows?: InfoSheetRow[][]
  /**
   * `always` keeps the operator gutter on every row (equations);
   * `auto` indents only rows that have an operator (breakdowns).
   */
  gutter?: 'always' | 'auto'
}

interface Props {
  content: InfoSheetContent
}

const props = defineProps<Props>()

const showGutter = (row: InfoSheetRow) =>
  (props.content.gutter ?? 'always') === 'always' || Boolean(row.operator)

// Every equation block — `rows` first, then any `extraRows` — rendered alike.
const equations = computed(() =>
  [props.content.rows ?? [], ...(props.content.extraRows ?? [])].filter((group) => group.length),
)

// A rule sits above each block's result row, separating it from the operands.
const resultIndexOf = (group: InfoSheetRow[]) => group.findIndex((row) => row.isResult)
</script>

<template>
  <Box direction="col" gap="4" class="info-sheet">
    <Box align="baseline" justify="between" gap="3" class="info-sheet__header">
      <Text is="h2" type="title-lg">{{ content.title }}</Text>
      <Text v-if="content.value" is="span" type="title-lg" weight="bold" class="info-sheet__value">
        <CurrencyText :text="content.value" />
      </Text>
    </Box>

    <Text v-if="content.subline" type="caption" class="info-sheet__subline">
      <CurrencyText :text="content.subline" />
    </Text>

    <Box v-if="content.note" align="center" justify="center" class="info-sheet__note">
      <Text
        is="span"
        type="caption-sm"
        color="var(--p-primary-700)"
        align="center"
        style="font-style: italic; line-height: 1.38"
      >
        <CurrencyText :text="content.note" />
      </Text>
    </Box>

    <Text v-if="content.description" type="caption" style="line-height: 18px"
      ><CurrencyText :text="content.description"
    /></Text>

    <Box
      v-for="(group, groupIndex) in equations"
      :key="groupIndex"
      direction="col"
      gap="2"
      class="info-sheet__rows"
    >
      <template v-for="(row, index) in group" :key="`${index}-${row.label}`">
        <Box v-if="index === resultIndexOf(group)" class="info-sheet__rule" />
        <Box align="center" gap="2" class="info-sheet__row">
          <Text
            v-if="showGutter(row)"
            is="span"
            type="body-sm"
            weight="semibold"
            color="var(--p-surface-400)"
            align="center"
            class="info-sheet__operator"
          >
            {{ row.operator ?? '' }}
          </Text>
          <Text
            is="span"
            type="body-sm"
            :weight="row.isResult ? 'semibold' : 'medium'"
            color="default"
          >
            {{ row.label }}
          </Text>
          <Text
            is="span"
            :type="row.isResult ? 'heading' : 'body-sm'"
            weight="semibold"
            color="default"
            class="info-sheet__amount"
          >
            <CurrencyText :text="row.value" />
          </Text>
        </Box>
      </template>
    </Box>

    <slot name="footer" />
  </Box>
</template>

<style scoped>
.info-sheet {
  width: 100%;
  padding: 14px 24px 30px;
  box-sizing: border-box;
  background: var(--p-surface-0);
}

.info-sheet__header {
  width: 100%;
}

.info-sheet__value {
  flex-shrink: 0;
  white-space: nowrap;
}

.info-sheet__subline {
  margin-top: -8px;
}

.info-sheet__note {
  width: 100%;
  padding: 9px 11px;
  border-radius: 9px;
  background: var(--p-primary-50);
}

.info-sheet__rows {
  width: 100%;
}

.info-sheet__row {
  min-height: 20px;
}

.info-sheet__operator {
  width: 14px;
  flex-shrink: 0;
}

/* Amounts right-align into one column so the sum reads like a receipt. */
.info-sheet__amount {
  margin-left: auto;
  white-space: nowrap;
}

.info-sheet__rule {
  width: 100%;
  height: 1.5px;
  background: var(--p-surface-300);
}
</style>
